import { IKIBusinessPriority } from 'legacy/lut/features/partner360/jbpSettings/models/kiBusinessPriority'

import { KeyInitiative } from 'partner360/entities/keyInitiatives'

import {
  buildBusinessPriorityUidToNameMap,
  getInitiativeBusinessPriorityNames,
} from '../helpers/activityBusinessPriorities'
import { IMdfDashboardFiltersForm } from '../ui/views/mdfDashboard/models'

import { normalizeSpaces } from './normalizeSpaces'
import { parseSeparatedString } from './parseSeparatedString'

interface IMdfPlannedFundsScope extends IMdfDashboardFiltersForm {
  partnerId: string
  fYear: number
  programType: string
  isUsd: boolean
}

const getQuarter = (value: string, fYear: number): string | null => {
  const match = /^(?:(?:FY)?(\d{4}|\d{2})\s*)?Q([1-4])$/i.exec(value.trim())
  if (!match) return null
  if (match[1] && Number(match[1]) !== (match[1].length === 2 ? fYear % 100 : fYear)) {
    return null
  }
  return `Q${match[2]}`
}

/** P360 funding plans, including initiatives for which no MDF activity exists yet. */
export const getMdfPlannedFunds = (
  initiatives: readonly KeyInitiative[],
  kiBusinessPriorities: IKIBusinessPriority[],
  {
    partnerId,
    fYear,
    programType,
    isUsd,
    strategicInitiatives,
    businessPriorities,
    timeframes,
  }: IMdfPlannedFundsScope
): number => {
  const priorityNames = buildBusinessPriorityUidToNameMap(kiBusinessPriorities)
  const quarters = new Set(timeframes.map((timeframe) => getQuarter(timeframe, fYear)))
  const countedIds = new Set<number>()

  return initiatives.reduce((total, initiative) => {
    const offerings = parseSeparatedString(initiative.ProgramType)
    if (
      // Parent distinguishes real initiatives from aggregate rows in the procedure response.
      !initiative.Parent ||
      initiative.PartnerId !== partnerId ||
      initiative.FYear !== fYear ||
      (offerings.length > 0 && !offerings.includes(programType)) ||
      initiative.HPInvestmentSource?.trim().toUpperCase() !== 'MDF' ||
      countedIds.has(initiative.EntryId)
    ) {
      return total
    }

    const initiativeStrategicKeys = [initiative.Development, initiative.KeyObjectiveCategory]
      .filter((key): key is string => Boolean(key))
      .map((key) => normalizeSpaces(key).trim())
    if (
      strategicInitiatives.length > 0 &&
      !strategicInitiatives.some((key) => initiativeStrategicKeys.includes(key))
    ) {
      return total
    }

    const initiativePriorityNames = getInitiativeBusinessPriorityNames(initiative, priorityNames)
    if (
      businessPriorities.length > 0 &&
      !businessPriorities.some((name) => initiativePriorityNames.includes(name))
    ) {
      return total
    }

    // There is no quarterly funding split: count each overlapping initiative's plan once.
    if (
      timeframes.length > 0 &&
      !parseSeparatedString(initiative.Period).some((period) => {
        const quarter = getQuarter(period, fYear)
        return quarter !== null && quarters.has(quarter)
      })
    ) {
      return total
    }

    countedIds.add(initiative.EntryId)
    // Same monetary fields as Cockpit MDF Funds. Never label a local amount as USD.
    const amount = isUsd ? initiative.HPInvestmentSumUSD : initiative.HPInvestmentSum
    return total + (typeof amount === 'number' && Number.isFinite(amount) ? amount : 0)
  }, 0)
}
